import { Dialog } from "primereact/dialog"
import { useCallback, useEffect, useRef, useState } from "react"
import Fuse from 'fuse.js';
import Tooltip from "../../../helpers/Tooltip";
import { v4 as uuidv4 } from 'uuid';
import { read, utils, writeFile } from 'xlsx';
import { useDispatch } from "react-redux";

import { AutoCompleteField, Button, DropDownField, TextInputField } from "../../../helpers";
import Menu from "../../../helpers/Menu";

import { inserVMIMaterialMaster } from "../../../services/api/materialMaster";
import { getVMIContractItems } from "../../../services/api/vmiContractItems";
import { raiseToast } from "../../../services/redux/reducers/toast/toast";

import { validateInputs } from "../../../utils";

import { vmiContractItemsBulkUploadValidation } from "../../../validation/vmiValidation";


const UploadVMIMaterialSheet = (props) => {

    const { setIsLoading, setVisibleUploadVMIMaterialSheet, companies, companyWarehouses } = props

    const [companyId, setCompanyId] = useState(null)
    const [warehouseId, setWarehouseId] = useState(null)
    const [companySuggestions, setCompanySuggestions] = useState([])
    const [vmiContractItems, setVMIContractItems] = useState([])
    const [prevVMIContractItems, setPrevVMIContractItems] = useState([])
    const [uploadFileLoading, setUploadFileLoading] = useState(false)

    const [formErrors, setFormErrors] = useState([])

    const fileInputRef = useRef(null)
    const uploadMenu = useRef(null)

    const dispatch = useDispatch()

    const uploadOptions = [
        {
            label: 'Download Sample Sheet',
            command: () => {
                let vmiProductData = prevVMIContractItems.map(eachVMIContractItem => {

                    let orderQty = ''
                    let targetStockQty = ''
                    if (eachVMIContractItem.VMIQtyTypeId === 1) {
                        orderQty = eachVMIContractItem.VMIQty
                    }
                    else if (eachVMIContractItem.VMIQtyTypeId === 2) {
                        targetStockQty = eachVMIContractItem.VMIQty
                    }

                    return {
                        "Company ERP Code": eachVMIContractItem.companyERPCode,
                        "Perm Material Id": eachVMIContractItem.permMaterialId,
                        "VMI Material Id": eachVMIContractItem.materialId,
                        "Ordering Threshold Qty": eachVMIContractItem.orderingThresholdQty,
                        "Safety Stock": eachVMIContractItem.safetyStock,
                        "Order Qty": orderQty,
                        "Target Stock Qty": targetStockQty
                    }
                })

                if (vmiProductData.length === 0) {
                    vmiProductData = [
                        {
                            "Company ERP Code": '',
                            "Perm Material Id": '',
                            "VMI Material Id": '',
                            "Ordering Threshold Qty": '',
                            "Safety Stock": '',
                            "Order Qty": '',
                            "Target Stock Qty": ''
                        }
                    ]
                }

                const worksheet = utils.json_to_sheet(vmiProductData)
                const workbook = utils.book_new()
                utils.book_append_sheet(workbook, worksheet, "Data")
                writeFile(workbook, "sample VMI product sheet.xlsx")
            }
        },
        {
            label: 'Upload File',
            command: () => {
                const fileInput = document.getElementById('upload-xl-form');
                if (fileInput) {
                    fileInput.click()
                }
            }
        }
    ]

    useEffect(() => {
        if (typeof (companyId) === 'number' && warehouseId) {
            getPrevVMIContractItems()
        }
    }, [companyId, warehouseId])

    const companyFuzzSearch = useCallback(new Fuse(companies, {
        keys: ['companyName'],
        includeScore: true,
        threshold: 0.5,
        ignoreLocation: true,
        shouldSort: true,
        getFn: (obj) => {
            return obj.companyName.replace(/\s+/g, '')
        },
    }), [companies])

    const getPrevVMIContractItems = async () => {
        setUploadFileLoading(true)
        const vmiContractItemsRes = await getVMIContractItems({
            companyIds: [companyId],
            warehouseIds: [warehouseId]
        })
        if (vmiContractItemsRes.failure) {
            setUploadFileLoading(false)
            setPrevVMIContractItems([])
            setVMIContractItems([])
            return dispatch(raiseToast({ severity: 'error', summary: 'VMI Contract Items', detail: vmiContractItemsRes.data.error }))
        }

        if (vmiContractItemsRes.length === 0) {
            setPrevVMIContractItems([])
            setVMIContractItems([])
        }
        setPrevVMIContractItems(vmiContractItemsRes)
        setUploadFileLoading(false)
    }

    const handleWarehouse = async (event) => {
        setWarehouseId(event.target.value)
    }

    const companySearchMethod = (event) => {
        if (event.query === "") {
            setCompanySuggestions([...companies])
        }
        else {
            const searchResults = companyFuzzSearch.search(event.query)
            const relatedCompanies = searchResults.map(eachProduct => eachProduct.item)
            setCompanySuggestions(relatedCompanies)
        }
    }

    const handleCompanyChange = (event) => {
        let companyObj = event.target.value
        if (typeof (event.target.value) === "object" && event.target.value !== null) {
            companyObj = event.target.value.companyId
        }

        setCompanyId(companyObj)
    }

    const uploadFileValidations = (parsedVMIMaterials) => {
        const isDataValid = parsedVMIMaterials.some((eachMaterial, index) => {
            if (!eachMaterial["Perm Material Id"]) {
                dispatch(raiseToast({ severity: 'error', detail: `Please enter Perm Material Id` }))
                return true
            }
            const materialCount = parsedVMIMaterials.filter(filteredMaterial => {
                return filteredMaterial["Perm Material Id"] === eachMaterial["Perm Material Id"]
            }).length
            if (materialCount > 1) {
                dispatch(raiseToast({ severity: 'error', detail: `Material ${eachMaterial["Perm Material Id"]} is entered more than once!` }))
                return true
            }
            if (!eachMaterial["Ordering Threshold Qty"]) {
                dispatch(raiseToast({ severity: 'error', detail: `Please enter Ordering Threshold Qty- material ${eachMaterial["Perm Material Id"]}` }))
                return true
            }
            if (!eachMaterial["Safety Stock"]) {
                dispatch(raiseToast({ severity: 'error', detail: `Please enter Minimum Stock Qty- material ${eachMaterial["Perm Material Id"]}` }))
                return true
            }
            if (parseFloat(eachMaterial["Safety Stock"]) && (parseFloat(eachMaterial["Safety Stock"]) > parseFloat(eachMaterial["Ordering Threshold Qty"]))) {
                dispatch(raiseToast({ severity: 'error', detail: `Minimum stock can't be greater than ordering threshold qty- material ${eachMaterial["Perm Material Id"]}` }))
                return true
            }
            if (parseFloat(eachMaterial["Target Stock Qty"]) && parseFloat(eachMaterial["Order Qty"])) {
                dispatch(raiseToast({ severity: 'error', detail: `Only enter Order Qty (or) Ordering Threshold Qty for material ${eachMaterial["Perm Material Id"]}` }))
                return true
            }
            if (parseFloat(eachMaterial["Target Stock Qty"]) && (parseFloat(eachMaterial["Target Stock Qty"]) < parseFloat(eachMaterial["Ordering Threshold Qty"]))) {
                dispatch(raiseToast({ severity: 'error', detail: `Target Stock Qty can't be lower than ordering threshold qty- material ${eachMaterial["Perm Material Id"]}` }))
                return true
            }
        })

        return !isDataValid
    }

    const handleUploadFile = (e) => {
        const reader = new FileReader()
        if (e.target.files[0]) {
            reader.readAsArrayBuffer(e.target.files[0])
            reader.onload = (e) => {
                const data = e.target.result

                const workbook = read(data)
                const worksheet = workbook.Sheets[workbook.SheetNames[0]]
                const parsedVMIMaterials = utils.sheet_to_json(worksheet)

                if (uploadFileValidations(parsedVMIMaterials)) {
                    const updatedVMIContractItems = parsedVMIMaterials.map(eachMaterial => {
                        const companyERPCode = eachMaterial["Company ERP Code"] ? String(eachMaterial["Company ERP Code"]) : null
                        const relatedPrevVMIContractItem = prevVMIContractItems.find(eachPrevVMIContractItem => {
                            return eachPrevVMIContractItem.companyERPCode === companyERPCode
                                && eachPrevVMIContractItem.permMaterialId === eachMaterial["Perm Material Id"]
                        })

                        return {
                            tempUUID: uuidv4(),
                            VMIContractItemId: relatedPrevVMIContractItem?.VMIContractItemId ? relatedPrevVMIContractItem?.VMIContractItemId : null,
                            companyERPCode: companyERPCode,
                            permMaterialId: eachMaterial["Perm Material Id"],
                            materialId: eachMaterial["VMI Material Id"] && relatedPrevVMIContractItem?.companyERPCodeMapId ? eachMaterial["VMI Material Id"] : null,
                            safetyStock: eachMaterial["Safety Stock"] ? parseFloat(eachMaterial["Safety Stock"]) : null,
                            orderingThresholdQty: parseFloat(eachMaterial["Ordering Threshold Qty"]) ? parseFloat(eachMaterial["Ordering Threshold Qty"]) : null,
                            orderQty: parseFloat(eachMaterial["Order Qty"]) ? parseFloat(eachMaterial["Order Qty"]) : null,
                            targetStockQty: parseFloat(eachMaterial["Target Stock Qty"]) ? parseFloat(eachMaterial["Target Stock Qty"]) : null,
                            companyERPCodeMapId: relatedPrevVMIContractItem?.companyERPCodeMapId ? relatedPrevVMIContractItem.companyERPCodeMapId : null,
                            warehouseId: relatedPrevVMIContractItem?.warehouseId ? relatedPrevVMIContractItem?.warehouseId : null
                        }
                    })
                    setVMIContractItems(updatedVMIContractItems)
                }
                else {
                    setVMIContractItems([])
                }

                if (fileInputRef.current) {
                    fileInputRef.current.value = null;
                }
            }
        }
        reader.onabort = () => console.log('file reading was aborted')
        reader.onerror = () => console.log('file reading has failed')

        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    }

    const handleVMIMaterialsSubmit = async () => {
        const validationRes = await validateInputs(vmiContractItemsBulkUploadValidation, vmiContractItems)
        if (validationRes) {
            if (validationRes.error) {
                setFormErrors(validationRes.error)
            }
            else {

                if (!companyId) {
                    return dispatch(raiseToast({ severity: 'error', detail: 'Please enter company!' }))
                }
                else if (!warehouseId) {
                    return dispatch(raiseToast({ severity: 'error', detail: 'Please enter warehouse!' }))
                }

                const invalidData = vmiContractItems.some(eachVMIContractItem => {
                    if (!eachVMIContractItem.orderingThresholdQty) {
                        dispatch(raiseToast({ severity: 'error', detail: `Please enter Ordering Threshold Qty- material ${eachVMIContractItem.permMaterialId}` }))
                        return true
                    }
                    if (!eachVMIContractItem.safetyStock) {
                        dispatch(raiseToast({ severity: 'error', detail: `Please enter Minimum Stock Qty- material ${eachVMIContractItem.permMaterialId}` }))
                        return true
                    }
                    if (parseFloat(eachVMIContractItem.safetyStock) && (parseFloat(eachVMIContractItem.safetyStock) > parseFloat(eachVMIContractItem.orderingThresholdQty))) {
                        dispatch(raiseToast({ severity: 'error', detail: `Minimum stock can't be greater than ordering threshold qty- material ${eachVMIContractItem.permMaterialId}` }))
                        return true
                    }
                    if (parseFloat(eachVMIContractItem.targetStockQty) && parseFloat(eachVMIContractItem.orderQty)) {
                        dispatch(raiseToast({ severity: 'error', detail: `Only enter Order Qty (or) Ordering Threshold Qty for material ${eachVMIContractItem.permMaterialId}` }))
                        return true
                    }
                    if (parseFloat(eachVMIContractItem.targetStockQty) && (parseFloat(eachVMIContractItem.targetStockQty) < parseFloat(eachVMIContractItem.orderingThresholdQty))) {
                        dispatch(raiseToast({ severity: 'error', detail: `Target Stock Qty can't be lower than ordering threshold qty- material ${eachVMIContractItem.permMaterialId}` }))
                        return true
                    }
                })

                if (invalidData) {
                    return
                }


                const updatedVMIContractItems = vmiContractItems.map(eachVMIContractItem => {
                    if (eachVMIContractItem.targetStockQty) {
                        return {
                            ...eachVMIContractItem, VMIQtyTypeId: 2, VMIQty: eachVMIContractItem.targetStockQty, companyId: companyId, warehouseId: warehouseId
                        }
                    }
                    else {
                        return {
                            ...eachVMIContractItem, VMIQtyTypeId: 1, VMIQty: eachVMIContractItem.orderQty, companyId: companyId, warehouseId: warehouseId
                        }
                    }
                })

                if (window.confirm("Are you sure you want to continue!?")) {
                    setIsLoading(true)
                    const materialMasterRes = await inserVMIMaterialMaster({
                        vmiContractItems: updatedVMIContractItems
                    })

                    if (materialMasterRes.failure) {
                        setIsLoading(false)
                        return dispatch(raiseToast({ severity: 'error', summary: 'Material Master', detail: materialMasterRes.data.error }))
                    }
                    else {
                        setIsLoading(false)
                        dispatch(raiseToast({ severity: 'success', detail: 'Material successfully updated!' }))
                        handleOnHideUploadVMIMaterialSheet()
                    }
                }
            }
        }
    }

    const handleOnHideUploadVMIMaterialSheet = () => {
        setCompanyId(null)
        setWarehouseId(null)
        setVMIContractItems([])
        setPrevVMIContractItems([])
        setVisibleUploadVMIMaterialSheet(false)
    }

    let companyName = companyId
    if (typeof (companyId) === "number") {
        companyName = companies.find(eachCompany => eachCompany.companyId === companyId)
    }


    return (
        <Dialog header="UPLOAD VMI MATERIAL SHEET" visible={props.visible} style={{ minHeight: "50%" }} className="w-8" onHide={handleOnHideUploadVMIMaterialSheet}>
            <div className="flex flex-row align-items-center">
                <div className="flex flex-column col-12 mb-2 lg:col-3 lg:mb-0">
                    <AutoCompleteField dropdown name="company" labelText="Company" value={companyName} field="companyName" dropdownButton ClassName="styled-autocomplete-dropdown-button" searchMethod={companySearchMethod} suggestions={companySuggestions} onChange={handleCompanyChange} forceSelection required />
                </div>
                <div className="flex flex-column col-12 mb-2 lg:col-3 lg:mb-0">
                    <DropDownField name="warehouseId" labelText="Warehouse" options={companyWarehouses} value={warehouseId} optionLabel="warehouseName" optionValue="warehouseId" onChange={handleWarehouse} required />
                </div>
                <div className="flex flex-column mt-3 ml-4">
                    <Menu id='upload-menu' model={uploadOptions} popup menuRef={uploadMenu} />
                    <Button
                        aria-controls="upload-menu"
                        onClick={(event) => uploadMenu.current.toggle(event)}
                        icon="pi pi-upload"
                        label="Upload File"
                        disabled={(typeof (companyId) === 'string' || !companyId) || !warehouseId}
                        tooltip={((typeof (companyId) === 'string' || !companyId) || !warehouseId) && 'Please enter company and warehouse'}
                        tooltipOptions={{ position: 'right', showOnDisabled: true }}
                        loading={uploadFileLoading}
                        className="w-9rem"
                    />
                    <input type="file" accept=".xlsx, .xls" className='hidden' id='upload-xl-form' onChange={handleUploadFile} ref={fileInputRef} />
                </div>
            </div>
            <div className='hr-line'></div>
            {vmiContractItems.length ?
                <>
                    <h5 className="text-600 mt-2 ml-1">Materials
                        <Tooltip target=".material-icon" position="right" content="Green border indicate that the material is new in the company VMI configuration" />
                        <i className="pi pi-info-circle material-icon ml-1"></i>
                    </h5>
                    {vmiContractItems.map((eachVMIContractItem, index) => {
                        let displayLabel = false
                        if (index === 0) {
                            displayLabel = true
                        }
                        return (
                            <div className={`flex flex-row`} key={eachVMIContractItem.tempUUID}>
                                <div className="flex flex-column col-12 mb-2 lg:col-2 lg:mb-0">
                                    <TextInputField className={`${!eachVMIContractItem.companyERPCodeMapId ? "border-green-400" : ''}`} name="companyERPCode" value={eachVMIContractItem.companyERPCode} errorMessage={formErrors.permMaterialId} keyfilter={"pnum"} labelText={displayLabel && "Company ERP Code"} disabled />
                                </div>
                                <div className="flex flex-column col-12 mb-2 lg:col-2 lg:mb-0">
                                    <TextInputField className={`${!eachVMIContractItem.companyERPCodeMapId ? "border-green-400" : ''}`} name="permMaterialId" value={eachVMIContractItem.permMaterialId} errorMessage={formErrors.permMaterialId} keyfilter={"pnum"} labelText={displayLabel && "Perm Material Id"} required disabled />
                                </div>
                                <div className="flex flex-column col-12 mb-2 lg:col-2 lg:mb-0">
                                    <TextInputField className={`${!eachVMIContractItem.companyERPCodeMapId ? "border-green-400" : ''}`} name="materialId" value={eachVMIContractItem.materialId} errorMessage={formErrors.permMaterialId} keyfilter={"pnum"} labelText={displayLabel && "VMI Material Id"} required disabled />
                                </div>
                                <div className="flex flex-column col-12 mb-2 lg:col-2 lg:mb-0">
                                    <TextInputField className={`${!eachVMIContractItem.companyERPCodeMapId ? "border-green-400" : ''}`} name="orderingThresholdQty" value={eachVMIContractItem.orderingThresholdQty} errorMessage={formErrors.orderingThresholdQty} keyfilter={"pnum"} labelText={displayLabel && "Ordering Threshold Qty"} required disabled />
                                </div>
                                <div className="flex flex-column col-12 mb-2 lg:col-2 lg:mb-0">
                                    <TextInputField className={`${!eachVMIContractItem.companyERPCodeMapId ? "border-green-400" : ''}`} name="targetStockQty" value={eachVMIContractItem.targetStockQty} errorMessage={formErrors.targetStockQty} keyfilter={"pnum"} labelText={displayLabel && "Target Stock Qty"} required disabled />
                                </div>
                                <div className="flex flex-column col-12 mb-2 lg:col-2 lg:mb-0">
                                    <TextInputField className={`${!eachVMIContractItem.companyERPCodeMapId ? "border-green-400" : ''}`} name="orderQty" value={eachVMIContractItem.orderQty} errorMessage={formErrors.orderQty} keyfilter={"pnum"} labelText={displayLabel && "Order Qty"} required disabled />
                                </div>
                                <div className="flex flex-column col-12 mb-2 lg:col-2 lg:mb-0">
                                    <TextInputField className={`${!eachVMIContractItem.companyERPCodeMapId ? "border-green-400" : ''}`} name="safetyStock" value={eachVMIContractItem.safetyStock} errorMessage={formErrors.safetyStock} labelText={displayLabel && "Safety Stock(optional)"} keyfilter={"pnum"} disabled />
                                </div>
                            </div>
                        )
                    })}
                    <div className='flex justify-content-center mt-2'>
                        <Button label="Add Materials" onClick={handleVMIMaterialsSubmit} />
                    </div>
                </>
                : null
            }
        </Dialog>
    )
}

export default UploadVMIMaterialSheet